import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import { format } from 'date-fns';
import useFetch from 'use-http';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import { InfoButton } from '@/components/ui/shared/InfoButton';

export default function PathologyResearchesTable() {
  const { t } = useTranslation();
  const history = useHistory();
  const sessionDispatch = useUserDispatch();
  const userState = useUserState();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(userState.dateFilter);

  const { get: getResearches, response: researchesResp } = useFetch(
    '/UserAccount/PathologyResearches',
  );
  const { get: getResearch } = useFetch('/UserAccount/PathologyResearch');

  /* ---------- data fetch ---------- */
  const fetchData = async (
    date = dateRange,
    term = search,
    filter = selectedFilter,
  ) => {
    setIsLoading(true);
    sessionDispatch({ type: 'UPDATE', data: { dateFilter: date } });

    const res = await getResearches(
      getQueryString(date, term, null, filter, userState.espbiEsi),
    );

    if (researchesResp.ok) {
      // NOTE: backend typo: `reaserches`
      const list = res?.reaserches ?? [];
      const sorted = _.orderBy(list, ['orderDate'], ['desc']);
      setData(sorted);

      const opts =
        res?.filters?.map((f) => ({ value: f.id, label: f.displayName })) ?? [];
      setFilters(opts);
    }
    setIsLoading(false);
  };

  /* ---------- row click ---------- */
  const handleSelection = async (id) => {
    setIsLoading(true);
    const query = userState.espbiEsi
      ? `?id=${id}&espbiEsi=${userState.espbiEsi}`
      : `?id=${id}`;
    const res = await getResearch(query);
    history.push('/userAccount/pathology-researches/details', { data: res });
    setIsLoading(false);
  };

  /* initial / date‑range refresh */
  useEffect(
    () => {
      fetchData();
    },
    [dateRange],
    userState?.espbiEsi,
  );

  return (
    <DataTable
      title="Patologiniai tyrimai"
      loading={isLoading}
      columns={columns({ handleSelection })}
      data={data}
      filterableColumns={['name', 'status.displayName', 'orderDate']}
      onRowClick={(row) => handleSelection(row.id)}
    />
  );
}

/* ------------- columns config ------------- */
const columns = ({ handleSelection }) => [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="TYRIMAS" />
    ),
    cell: ({ row }) => (
      <span className="font-medium max-w-[400px] truncate capitalize">
        {row.original.name}
      </span>
    ),
  },
  {
    accessorKey: 'orderDate',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => {
      const d = new Date(row.original.orderDate);
      const isMidnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0;
      return (
        <span className="text-muted-foreground">
          {isMidnight ? format(d, 'yyyy-MM-dd') : format(d, 'yyyy-MM-dd HH:mm')}
        </span>
      );
    },
  },
  {
    accessorKey: 'status.displayName',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="BŪSENA" />
    ),
    cell: ({ row }) => (
      <Badge variant="neutral">{row.original.status.displayName}</Badge>
    ),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex justify-end">
        <InfoButton
          onClick={(e) => {
            e.stopPropagation();
            handleSelection(row.original.id);
          }}
        >
          Informacija
        </InfoButton>
      </div>
    ),
  },
];
